import React, { useState } from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Button, Chip, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const pricingCategories = [
  {
    category: 'Device Repair',
    services: [
      { name: 'Screen Replacement (Laptop)',      price: 'From $79',  note: 'Parts included' },
      { name: 'Battery Replacement (Laptop/Mac)', price: 'From $59',  note: 'Parts included' },
      { name: 'Virus Removal & Cleanup',          price: '$49',       note: 'Remote or on-site' },
      { name: 'OS Reinstallation',                price: '$59',       note: 'Data preserved' },
      { name: 'Smartphone Screen Repair',         price: 'From $69',  note: 'Parts included' },
      { name: 'Data Recovery',                    price: 'From $99',  note: 'Quote after diagnosis' },
      { name: 'Hardware Diagnostics',             price: '$29',       note: 'Free if we do the repair' },
      { name: 'Liquid Damage Treatment',          price: 'From $89',  note: 'Quote after assessment' },
    ],
  },
  {
    category: 'IT & Networking',
    services: [
      { name: 'Network Setup (Home)',             price: '$99',       note: 'Router + up to 4 devices' },
      { name: 'Network Setup (Business)',         price: 'From $199', note: 'Custom quote' },
      { name: 'WiFi Optimization',                price: '$79',       note: 'Site survey included' },
      { name: 'Firewall Configuration',           price: 'From $149', note: 'ASA, Pfsense, SonicWall' },
      { name: 'VPN Setup',                        price: 'From $99',  note: 'Per site' },
      { name: 'Network Security Audit',           price: 'From $249', note: 'Full report included' },
      { name: 'NAS / Storage Setup',              price: 'From $149', note: 'Configuration + mapping' },
    ],
  },
  {
    category: 'Cloud & Microsoft 365',
    services: [
      { name: 'M365 Business Setup',             price: 'From $199', note: 'Exchange, Teams, SharePoint' },
      { name: 'Azure AD / Entra ID',             price: 'From $149', note: 'Per tenant' },
      { name: 'Intune MDM Enrollment',           price: 'From $99',  note: 'Up to 10 devices' },
      { name: 'Email Migration',                 price: 'From $149', note: 'Per mailbox' },
      { name: 'SharePoint / OneDrive Setup',     price: 'From $199', note: 'Custom structure' },
      { name: 'Hybrid AD Sync',                  price: 'From $299', note: 'Entra Connect' },
    ],
  },
  {
    category: 'Support Plans',
    services: [
      { name: 'Basic Remote Support',            price: '$49/hr',    note: 'Pay as you go' },
      { name: 'On-Site Visit',                   price: '$89/hr',    note: 'Lynn, MA & surrounding' },
      { name: 'Monthly IT Support — Starter',   price: '$199/mo',   note: 'Up to 5 hrs remote' },
      { name: 'Monthly IT Support — Business',  price: '$399/mo',   note: 'Up to 10 hrs + priority' },
      { name: 'Emergency After-Hours',           price: '$129/hr',   note: 'Weekend / evening' },
    ],
  },
];

const highlights = [
  'Free diagnostics when we do the repair',
  '90-day warranty on all parts & labor',
  'No fix, no fee policy on diagnostics',
  'Transparent pricing — no hidden fees',
  'On-site service in Lynn, MA & surrounding areas',
  'Remote support available nationwide',
];

function Pricing() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: '8%', right: '4%',
        width: 400, height: 400,
        background: 'radial-gradient(circle, rgba(255,107,107,0.1) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, py: 10 }}>
        <Container maxWidth="lg">

          {/* Hero */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, display: 'block', mb: 2 }}>
              Transparent Pricing
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.4rem' }, fontWeight: 800, color: '#fff', mb: 2 }}>
              Simple,{' '}
              <Box component="span" className="gradient-text">Honest Pricing</Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', lineHeight: 1.8 }}>
              No hidden fees. No surprises. Just fair pricing for expert IT services.
            </Typography>
          </Box>

          {/* Highlights bar */}
          <Box sx={{
            background: 'rgba(255,107,107,0.04)',
            border: '1px solid rgba(255,107,107,0.1)',
            borderRadius: '16px',
            p: 3, mb: 8,
          }}>
            <Grid container spacing={2} justifyContent="center">
              {highlights.map((h, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>{h}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Category tabs */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 5 }}>
            {pricingCategories.map((cat, i) => (
              <Chip
                key={i}
                label={cat.category}
                onClick={() => setActive(i)}
                sx={{
                  px: 1,
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  background: active === i
                    ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)'
                    : 'rgba(255,107,107,0.08)',
                  color: active === i ? '#2d3436' : '#fff',
                  border: active === i ? 'none' : '1px solid rgba(255,107,107,0.2)',
                  cursor: 'pointer',
                  '&:hover': { background: active === i ? undefined : 'rgba(255,107,107,0.15)' },
                }}
              />
            ))}
          </Box>

          {/* Pricing table */}
          <Card sx={{
            background: 'rgba(255,107,107,0.04)',
            border: '1px solid rgba(255,107,107,0.12)',
            borderRadius: '18px',
            backdropFilter: 'blur(10px)',
            mb: 8,
          }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,107,107,0.1)' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                  {pricingCategories[active].category}
                </Typography>
              </Box>
              {pricingCategories[active].services.map((svc, i) => (
                <React.Fragment key={i}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 3, py: 2.2,
                    '&:hover': { background: 'rgba(255,107,107,0.04)' },
                  }}>
                    <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 500 }}>{svc.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{svc.note}</Typography>
                    </Box>
                    <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1rem', whiteSpace: 'nowrap', ml: 2 }}>
                      {svc.price}
                    </Typography>
                  </Box>
                  {i < pricingCategories[active].services.length - 1 && (
                    <Divider sx={{ borderColor: 'rgba(255,107,107,0.06)' }} />
                  )}
                </React.Fragment>
              ))}
            </CardContent>
          </Card>

          {/* CTA */}
          <Box sx={{
            textAlign: 'center', p: { xs: 4, md: 6 },
            background: 'rgba(255,107,107,0.05)',
            border: '1px solid rgba(255,107,107,0.15)',
            borderRadius: '20px',
          }}>
            <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 700, mb: 1.5 }}>
              Need a Custom Quote?
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              Every business is different. Contact us for a free assessment and tailored pricing.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/book-service')}
                sx={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
                  color: '#2d3436', fontWeight: 700, px: 4,
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(255,107,107,0.3)' },
                  transition: 'all 0.3s ease',
                }}
              >
                Book a Service
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/contact')}
                sx={{
                  borderColor: 'rgba(255,107,107,0.4)', color: '#fff', px: 4,
                  '&:hover': { borderColor: 'primary.main', background: 'rgba(255,107,107,0.08)' },
                }}
              >
                Contact Us
              </Button>
            </Box>
          </Box>

        </Container>
      </Box>
    </Box>
  );
}

export default Pricing;
